// Estimated trading charges for Indian index-option trades (discount-broker /
// Zerodha-style). CALIBRATED against a real Zerodha F&O P&L statement (NIFTY
// options, June 2026) — the rates below reproduce that statement's total charges
// to within ~0.2%. The GST formula and brokerage/stamp/SEBI matched exactly; STT
// and the exchange charge were reverse-engineered from the statement's buy/sell
// values. These are estimates — actual charges vary slightly by broker & exchange;
// tweak the rates here in one place.
//
//  • Brokerage: flat ₹20 per executed order (F&O); a leg = entry + exit = 2 orders.
//  • STT: 0.125% of the SELL-side option premium (statement implied ~0.124%).
//  • Exchange txn (NSE options): 0.03503% of premium turnover (buy + sell).
//  • SEBI: ₹10 per crore = 0.0001% of turnover.
//  • GST: 18% on (brokerage + exchange txn + SEBI) — verified exact on the statement.
//  • Stamp duty: 0.003% on the BUY-side premium turnover.
export const CHARGE_RATES = {
  brokeragePerOrder: 20,    // ₹ per executed order; a leg = entry + exit = 2 orders
  sttSellPct: 0.00125,      // 0.125% of SELL-side premium value
  exchTxnPct: 0.0003503,    // 0.03503% of total premium turnover (buy + sell) — NSE statutory option txn charge; matches a real 1-yr statement (0.03474% effective)
  sebiPct: 0.000001,        // 0.0001%
  gstPct: 0.18,             // on brokerage + exch txn + SEBI
  stampBuyPct: 0.00003,     // 0.003% of BUY-side premium turnover
};

// Cash-equity INTRADAY (MIS) charges — completely different from F&O. The equity
// backtester trades the share price itself, so "value" = share price × quantity
// (full notional), NOT a small option premium. Applying the option rates above to
// that notional massively over-states charges, so equity uses this dedicated set:
//  • Brokerage: 0.03% of order value or ₹20, whichever is LOWER, per executed order.
//  • STT: 0.025% of the SELL-side value only (intraday).
//  • Exchange txn (NSE cash): 0.00297% of turnover (buy + sell).
//  • SEBI: ₹10 per crore = 0.0001% of turnover.
//  • GST: 18% on (brokerage + exchange txn + SEBI).
//  • Stamp duty: 0.003% on the BUY-side value (intraday).
export const EQUITY_INTRADAY_RATES = {
  brokeragePct: 0.0003,     // 0.03% of order value …
  brokerageCap: 20,         // … capped at ₹20 per executed order
  sttSellPct: 0.00025,      // 0.025% of SELL-side value (intraday)
  exchTxnPct: 0.0000297,    // 0.00297% of turnover (NSE cash)
  sebiPct: 0.000001,        // 0.0001%
  gstPct: 0.18,             // on brokerage + exch txn + SEBI
  stampBuyPct: 0.00003,     // 0.003% of BUY-side value (intraday)
};

// Index / stock FUTURES (NSE F&O) intraday/positional charges — different again
// from both options and cash. The futures backtester trades the future price itself,
// so "value" = future price × quantity (full contract notional, e.g. ~26000 × 65),
// NOT an option premium — so the option STT/txn rates would massively over-charge.
//  • Brokerage: 0.03% of order value or ₹20, whichever is LOWER, per executed order.
//  • STT: 0.02% of the SELL-side value only (futures).
//  • Exchange txn (NSE futures): 0.00173% of turnover (buy + sell).
//  • SEBI: ₹10 per crore = 0.0001% of turnover.
//  • GST: 18% on (brokerage + exchange txn + SEBI).
//  • Stamp duty: 0.002% on the BUY-side value.
export const FUTURES_RATES = {
  brokeragePct: 0.0003,     // 0.03% of order value …
  brokerageCap: 20,         // … capped at ₹20 per executed order
  sttSellPct: 0.0002,       // 0.02% of SELL-side value (futures)
  exchTxnPct: 0.0000173,    // 0.00173% of turnover (NSE futures)
  sebiPct: 0.000001,        // 0.0001%
  gstPct: 0.18,             // on brokerage + exch txn + SEBI
  stampBuyPct: 0.00002,     // 0.002% of BUY-side value
};

// Delta Exchange (India) fees — from delta.exchange/fees (2026). Completely
// different from NSE: NO brokerage-per-order, NO STT, NO stamp — just a % trading
// fee + 18% GST on that fee:
//   • Futures / Perpetual: TAKER 0.05% of notional (price × size), both legs.
//   • Options: TAKER 0.03% of the UNDERLYING notional (spot × size), CAPPED at
//     3.5% of the option premium, both legs.
//   • GST: 18% on the trading fee.
// (Maker is cheaper — 0.02% / 0.01% — but we assume taker = worst case. India's
//  1% TDS + 30% gains tax are settlement/income taxes, not per-trade costs.)
export const CRYPTO_RATES = {
  futuresTakerPct: 0.0005,     // 0.05% of notional (perp / futures) — MARKET order
  optionTakerPct: 0.0003,      // 0.03% of underlying notional — MARKET order
  futuresMakerPct: 0.0002,     // 0.02% — LIMIT order that adds liquidity (much cheaper)
  optionMakerPct: 0.0001,      // 0.01% — LIMIT order
  optionPremiumCapPct: 0.035,  // capped at 3.5% of the premium
  gstPct: 0.18,
};

// One crypto leg (entry order + exit order). `qty` already includes the contract
// value (e.g. 100 BTC contracts → qty 0.1 BTC), so price × qty and spot × qty are
// USD values and the fee comes out in USD — matching the backtest's USD P&L.
// `spotEntry/spotExit` are the underlying spot at each leg (needed for option
// notional); for a perp the price IS the underlying, so they're unused.
function cryptoLegCharges(entryPrice, exitPrice, qty, assetClass, spotEntry, spotExit, feeMode) {
  const R = CRYPTO_RATES;
  const maker = feeMode === 'maker'; // LIMIT orders pay the (lower) maker fee
  let fee;
  if (String(assetClass) === 'crypto_perp') {
    const rate = maker ? R.futuresMakerPct : R.futuresTakerPct;
    fee = rate * ((entryPrice * qty) + (exitPrice * qty)); // % of notional, both legs
  } else {
    // Options: per side, min(rate × underlying notional, 3.5% of premium).
    const rate = maker ? R.optionMakerPct : R.optionTakerPct;
    const sE = spotEntry || entryPrice, sX = spotExit || exitPrice;
    const side = (spot, premium) => Math.min(rate * spot * qty, R.optionPremiumCapPct * premium * qty);
    fee = side(sE, entryPrice) + side(sX, exitPrice);
  }
  return fee + R.gstPct * fee;
}

// Index/stock futures: same shape as equity intraday but the F&O rate table (0.02%
// sell-side STT, NSE-futures txn charge). "value" = future price × qty.
function futureLegCharges(entryPrice, exitPrice, qty, action) {
  const R = FUTURES_RATES;
  const sell = String(action).toUpperCase() === 'SELL';
  const sellVal = (sell ? entryPrice : exitPrice) * qty;
  const buyVal = (sell ? exitPrice : entryPrice) * qty;
  const turnover = sellVal + buyVal;
  const brokerage = Math.min(R.brokeragePct * sellVal, R.brokerageCap)
                  + Math.min(R.brokeragePct * buyVal, R.brokerageCap);
  const stt = R.sttSellPct * sellVal;
  const exch = R.exchTxnPct * turnover;
  const sebi = R.sebiPct * turnover;
  const gst = R.gstPct * (brokerage + exch + sebi);
  const stamp = R.stampBuyPct * buyVal;
  return brokerage + stt + exch + sebi + gst + stamp;
}

function equityLegCharges(entryPrice, exitPrice, qty, action) {
  const R = EQUITY_INTRADAY_RATES;
  const sell = String(action).toUpperCase() === 'SELL';
  const sellVal = (sell ? entryPrice : exitPrice) * qty;
  const buyVal = (sell ? exitPrice : entryPrice) * qty;
  const turnover = sellVal + buyVal;
  // Brokerage is per-order = min(0.03% of that order's value, ₹20). A leg = 2 orders.
  const brokerage = Math.min(R.brokeragePct * sellVal, R.brokerageCap)
                  + Math.min(R.brokeragePct * buyVal, R.brokerageCap);
  const stt = R.sttSellPct * sellVal;
  const exch = R.exchTxnPct * turnover;
  const sebi = R.sebiPct * turnover;
  const gst = R.gstPct * (brokerage + exch + sebi);
  const stamp = R.stampBuyPct * buyVal;
  return brokerage + stt + exch + sebi + gst + stamp;
}

// Charges for ONE leg (one entry order + one exit order). `qty` is the contract
// quantity (already lots × lot-size). `action` = 'SELL' or 'BUY'. "value" =
// price × qty. For a SELL leg the entry is the sell; for a BUY leg the exit is.
// `assetClass` = 'equity' switches to the cash-equity intraday rate table.
export function legCharges(entryPrice, exitPrice, qty, action, assetClass, spotEntry, spotExit, feeMode) {
  if (String(assetClass).startsWith('crypto')) return cryptoLegCharges(entryPrice, exitPrice, qty, assetClass, spotEntry, spotExit, feeMode);
  if (String(assetClass) === 'equity') return equityLegCharges(entryPrice, exitPrice, qty, action);
  if (String(assetClass) === 'future') return futureLegCharges(entryPrice, exitPrice, qty, action);
  const R = CHARGE_RATES;
  const sell = String(action).toUpperCase() === 'SELL';
  const sellVal = (sell ? entryPrice : exitPrice) * qty;
  const buyVal = (sell ? exitPrice : entryPrice) * qty;
  const turnover = sellVal + buyVal;
  const brokerage = R.brokeragePerOrder * 2;
  const stt = R.sttSellPct * sellVal;
  const exch = R.exchTxnPct * turnover;
  const sebi = R.sebiPct * turnover;
  const gst = R.gstPct * (brokerage + exch + sebi);
  const stamp = R.stampBuyPct * buyVal;
  return brokerage + stt + exch + sebi + gst + stamp;
}

// The rate table for ONE leg. A futures leg (e.g. the Gamma-Scalp hedge, symbol
// "NIFTYFUT #N" / optType 'FUT') must be charged on the FUTURES table (0.02% sell-STT,
// 0.00173% txn on notional) — NOT the option-premium rates of its parent straddle,
// which would treat the whole futures notional as "premium" and over-charge ~8×. The
// leg's own type wins over the trade-level assetClass.
export function assetClassForLeg(leg, tradeAssetClass) {
  if (leg && (leg.optType === 'FUT' || /FUT/.test(leg.symbol || ''))) return 'future';
  return tradeAssetClass;
}

// Total charges across an array of legs. Each leg: { entryPrice|entryPremium,
// exitPrice|exitPremium, qty, action }. Skips legs without a price/qty.
export function legsCharges(legs, assetClass, feeMode) {
  let total = 0;
  for (const l of (legs || [])) {
    const qty = l.qty || 0;
    const entry = l.entryPrice != null ? l.entryPrice : (l.entryPremium != null ? l.entryPremium : 0);
    const exit = l.exitPrice != null ? l.exitPrice : (l.exitPremium != null ? l.exitPremium : 0);
    if (!qty || (!entry && !exit)) continue;
    total += legCharges(entry, exit, qty, l.action || 'SELL', assetClassForLeg(l, assetClass), l.spotEntry, l.spotExit, feeMode);
  }
  return total;
}

// Total charges across a backtest/sim tradeLog (array of trades, each with .legs).
// Each trade may carry its own `.assetClass` (set by the backend) — that wins, so a
// combined log of equity + option strategies is charged correctly per trade. The
// `assetClass` argument is the fallback for trades without their own tag.
export function tradeLogCharges(tradeLog, assetClass, feeMode) {
  let total = 0;
  for (const t of (tradeLog || [])) total += legsCharges(t.legs, t.assetClass || assetClass, feeMode);
  // Crypto charges are tiny USD amounts ($0.x) — keep 2 decimals so the summary
  // "Gross − Charges = Net" reconciles with the per-leg/per-day decimal charges.
  // NSE ₹ charges round to whole rupees as before.
  const isCrypto = String((tradeLog || []).find(t => t.assetClass)?.assetClass || assetClass || '').startsWith('crypto');
  return isCrypto ? Math.round(total * 100) / 100 : Math.round(total);
}
